import React, { useRef, useState, useCallback, useEffect } from 'react'
import {
  Button, Typography,
  Paper, CircularProgress, Divider, Slider, Tooltip, TextField, IconButton,
  Select, MenuItem, Snackbar,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import DeleteIcon from '@material-ui/icons/Delete'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop'
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom'
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter'
import FormatLineSpacingIcon from '@material-ui/icons/FormatLineSpacing'
import { getOverlayFields, previewOverlay } from '../../../Apis/overlays'
import { previewDefaultOverlay } from '../../../Apis/defaultOverlays'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import PlacementBadge from './PlacementBadge'
import FieldPalette from './FieldPalette'
import TextStyleControls from './TextStyleControls'
import MultiSelectPanel from './MultiSelectPanel'
import SelectByType from './SelectByType'
import { BoxSizeControls, BarFillControl, ImageSizeControls } from './TypeControls'
import { AlignRow, StepperRow } from './panelControls'
import { sentenceCase } from './fieldGroups'
import { positionStyle, DEFAULT_CROP } from './overlayLayout'
import { useOverlayDataSource } from './useOverlayDataSource'
import {
  alignToAnchor,
  distributeHorizontal, distributeVertical,
} from './alignment'
import { findSnapTargets, findSmartGuide } from './snap'
import { SPONSOR_DEFAULT_PER_ROW, displayValue, locksAspectByDefault,
  mergeSvgGeometry, newFieldPlacement, svgRectToPlacement } from './overlayEditorControls'
import { buildPreviewRequest } from './previewRequest'
import { labelFor, SLUGS, variantLabelForSlug } from '../../../utils/constants/overlayTypes'
import { checkerboard } from './checkerboard'

// Reads a File (or blob: object URL) to a data-URI. The backend renders the
// SVG background server-side by fetching `templateSvgUrl` — it has no access
// to the browser's blob: store, so an unsaved/just-uploaded background must
// be inlined as a data-URI before it's sent to the preview endpoint. Saved
// backgrounds (https:// S3 URLs) or already-data: URIs are returned as-is.
function fileToDataUri(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

async function resolvePreviewBackground({ pendingFile, displayUrl }) {
  if (pendingFile) return fileToDataUri(pendingFile)
  if (displayUrl?.startsWith('blob:')) {
    const resp = await fetch(displayUrl)
    const blob = await resp.blob()
    return fileToDataUri(blob)
  }
  return displayUrl
}

// Fallback font size/color for SVG-auto-detected text fields that don't carry
// their own data-font-size / data-color / computed-style values. The editor no
// longer exposes a card-level "Defaults" panel — every field's Size/Color live
// on its own placement — so these are just a starting point.
const DEFAULT_FONT_SIZE = 32
const DEFAULT_COLOR = '#ffffff'

const POSITIONS = [
  ['top-left',    'top-center',    'top-right'],
  ['center-left', 'center',       'center-right'],
  ['bottom-left', 'bottom-center', 'bottom-right'],
]

const CORNERS = [
  { id: 'top-left',     cursor: 'nwse-resize', v: 'top',    h: 'left'  },
  { id: 'top-right',    cursor: 'nesw-resize', v: 'top',    h: 'right' },
  { id: 'bottom-left',  cursor: 'nesw-resize', v: 'bottom', h: 'left'  },
  { id: 'bottom-right', cursor: 'nwse-resize', v: 'bottom', h: 'right' },
]

// A bar's x names the edge hAlign points at (not a box corner) and its y is the
// vertical centre — derive the box's actual left/top from that anchor.
const barLeft = (anchorX, w, hAlign) =>
  hAlign === 'right' ? anchorX - w : hAlign === 'center' ? anchorX - w / 2 : anchorX

// Visual bounds of a placement in canvas-percent coords. Box-model text/image
// placements carry their top-left (x, y) directly, so bounds are just the box
// rect; a bar's (x, y) is an anchor edge + vertical centre instead (see
// barLeft above). Used by the marquee to decide which placements intersect
// the drawn rectangle.
function getPlacementBounds(p, field) {
  const isImage = field?.dataType === 'Image'
  const isBar = field?.dataType === 'Bar'
  const w = isImage ? (p.imageWidth ?? 8) : (p.w ?? 6)
  const h = isImage ? (p.imageHeight ?? 8) : (p.h ?? 3)
  const left = isBar ? barLeft(p.x, w, p.hAlign) : p.x
  const top = isBar ? p.y - h / 2 : p.y
  return { left, top, right: left + w, bottom: top + h }
}

// Visual bounds in canvas-percent at a hypothetical (x, y) — used by the
// snap-to-element helper. Lets the caller override the in-progress position
// rather than reading p.x / p.y (which haven't yet been updated during a
// drag). Box-model text/image: (x, y) is the box's top-left. A bar: (x, y) is
// its anchor edge + vertical centre (see barLeft above).
function getSnapBounds(p, field, x, y) {
  const isImage = field?.dataType === 'Image'
  const isBar = field?.dataType === 'Bar'
  const w = isImage ? (p.imageWidth ?? 8) : (p.w ?? 6)
  const h = isImage ? (p.imageHeight ?? 8) : (p.h ?? 3)
  const left = isBar ? barLeft(x, w, p.hAlign) : x
  const top = isBar ? y - h / 2 : y
  const right = left + w
  const centerX = left + w / 2
  return { left, centerX, right, top, centerY: top + h / 2, bottom: top + h }
}

// Emits 'team' (track the team's own colour) or a #rrggbb literal — the only two shapes
// the backend accepts. See BarColor.IsValid.
function BarColorPicker({ label, value, onChange }) {
  const isTeam = !value || value === 'team'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Typography variant='caption' color='textSecondary' style={{ whiteSpace: 'nowrap' }}>{label}</Typography>
      <ToggleButtonGroup
        value={isTeam ? 'team' : 'custom'}
        exclusive
        size='small'
        onChange={(_, v) => v && onChange(v === 'team' ? 'team' : '#7D66FC')}
      >
        <ToggleButton value='team' style={{ fontSize: 11, padding: '4px 8px', textTransform: 'none' }}>Team</ToggleButton>
        <ToggleButton value='custom' style={{ fontSize: 11, padding: '4px 8px', textTransform: 'none' }}>Custom</ToggleButton>
      </ToggleButtonGroup>
      {!isTeam && (
        <input
          type='color'
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          style={{ border: 'none', cursor: 'pointer', width: 32, height: 24 }}
        />
      )}
    </div>
  )
}

export default function OverlayEditor({
  mode = 'season',
  orgId,
  seasonId,
  type,
  variant = null,
  // Every variant number the open type has, in order (e.g. Team Shots: [1, 2]). Drives
  // the Home/Away selector below — a type with one variant shows no selector at all.
  // The default-overlays page doesn't pass this; it has its own external variant picker.
  availableVariants,
  fields: initialFields,
  sport,          // default mode: which sport's board is open (only the scoreboard has two)
  statsMode,      // default mode: which stats mode's board (only Team Comparison has two)
  initialOverlay, // null or { templateSvgUrl, placements, targetMode }
  supportsTargetMode,
  defaultTargetMode,
  onSave,
  onCancel,
  onVariantChange, // (nextVariant) => void — switches the open side (season mode only)
}) {
  const isDefaultMode = mode === 'default'
  const variantOptions = availableVariants?.length > 0 ? availableVariants : (variant != null ? [variant] : [])
  const hasVariantSelector = variantOptions.length > 1
  const dataSource = useOverlayDataSource({ mode, orgId, seasonId, type, variant, sport, statsMode })
  const canvasRef = useRef(null)
  const canvasColumnRef = useRef(null)
  const svgWrapperRef = useRef(null)
  const hintRef = useRef(null)
  const [sidebarHeight, setSidebarHeight] = useState(null)
  // Canonical S3 URL (existing) or null for new overlays — sent on save/preview exactly
  // as stored. Never fetched directly from the browser: the platform S3 bucket is
  // private, so the raw URL 403s for an anonymous request.
  const [savedSvgUrl, setSavedSvgUrl] = useState(initialOverlay?.templateSvgUrl ?? null)
  // Presigned GET counterpart of savedSvgUrl, for actually loading the background in the
  // browser (img src, SVG-parse fetch). Falls back to the canonical URL when the
  // response carries no read URL.
  const [savedSvgReadUrl, setSavedSvgReadUrl] = useState(
    initialOverlay?.templateSvgReadUrl ?? initialOverlay?.templateSvgUrl ?? null)
  // Local object URL for preview before upload
  const [previewUrl, setPreviewUrl] = useState(null)
  // The File object waiting to be uploaded on Save
  const [pendingFile, setPendingFile] = useState(null)
  const [placements, setPlacements] = useState(initialOverlay?.placements ?? [])
  const layout = initialOverlay?.templateLayout
  const [templateScale, setTemplateScale] = useState(layout?.templateScale ?? 100)
  const [templatePosition, setTemplatePosition] = useState(layout?.templatePosition ?? 'center')
  const [templateCrop, setTemplateCrop] = useState(layout?.templateCrop ?? DEFAULT_CROP)
  // Free placement: percent of the canvas, top-left of the template box (same
  // convention as a field placement's x/y). Null until the user drags the
  // template or the loaded overlay already carries one; wins over
  // templatePosition when set (see positionStyle).
  const [templateX, setTemplateX] = useState(layout?.x ?? null)
  const [templateY, setTemplateY] = useState(layout?.y ?? null)
  // Bar fill colour(s) for this board: 'team' (track the team's own colour, the
  // default) or a #rrggbb literal — the only two shapes the backend accepts.
  const [homeBarColor, setHomeBarColor] = useState(layout?.homeBarColor ?? 'team')
  const [awayBarColor, setAwayBarColor] = useState(layout?.awayBarColor ?? 'team')
  const [barColor, setBarColor] = useState(layout?.barColor ?? 'team')
  // Placements/layout as the board was opened, compared against live state by isDirty.
  const openedSnapshotRef = useRef({
    placements: initialOverlay?.placements ?? [],
    templateScale: layout?.templateScale ?? 100,
    templatePosition: layout?.templatePosition ?? 'center',
    templateCrop: layout?.templateCrop ?? DEFAULT_CROP,
    templateX: layout?.x ?? null,
    templateY: layout?.y ?? null,
    homeBarColor: layout?.homeBarColor ?? 'team',
    awayBarColor: layout?.awayBarColor ?? 'team',
    barColor: layout?.barColor ?? 'team',
    savedSvgUrl: initialOverlay?.templateSvgUrl ?? null,
  })
  const [targetMode, setTargetMode] = useState(initialOverlay?.targetMode ?? defaultTargetMode ?? 'Single')
  const [fields, setFields] = useState(initialFields)
  const [dragging, setDragging] = useState(null)
  // Whole-template drag state: { startMouse: {x, y}, startX, startY } in
  // canvas-percent, or null. Distinct from `dragging` (crop-corner) above so
  // the two gestures can't fight over the same state.
  const [templateDrag, setTemplateDrag] = useState(null)
  // Ephemeral preview text per text field — never written into the placement,
  // just fed to the (future) Preview render toggle. Keyed by fieldId.
  const [previewText, setPreviewText] = useState({})
  // Ephemeral aspect-lock toggle per image field — a UI-only interaction with
  // no render meaning, so (like previewText) it's never written into the
  // placement / save payload. Keyed by fieldId. Fields whose art has a fixed upload
  // crop start locked: changing such a box's aspect can only letterbox the image
  // inside it, so an unlocked corner drag is a footgun with no upside.
  const [aspectLocked, setAspectLocked] = useState({})
  const isAspectLocked = (fieldId) => aspectLocked[fieldId] ?? locksAspectByDefault(fieldId)
  const setAspectLock = (ids, locked) =>
    setAspectLocked((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, locked])) }))
  const [fieldSearch, setFieldSearch] = useState('')
  const [pendingField, setPendingField] = useState(null)
  // True while the cursor is over (or near) the placement hint pill, so it
  // fades out of the way instead of blocking the click underneath it.
  const [hintFaded, setHintFaded] = useState(false)
  // Multi-select: an order-preserving array of fieldIds, in click order — the
  // LAST entry is the "anchor" for align-to-last-selected (see alignment.js's
  // alignToAnchor). The right-panel inspector keys off a derived single id
  // (when exactly one placement is selected). All other UI (selection outline,
  // Backspace-delete, alignment toolbar) operates on this array.
  const [selection, setSelection] = useState(() => [])
  const selectedFieldId = selection.length === 1 ? selection[0] : null
  // Add fieldId to the selection, keeping it last (the anchor). Idempotent.
  const addToSelection = (prev, fieldId) => [...prev.filter((id) => id !== fieldId), fieldId]
  // Template (background artwork) selection — mutually exclusive with field
  // selection. Gates the scale/anchor/crop controls (only usable while true).
  const [templateSelected, setTemplateSelected] = useState(false)
  // Any field-selection change clears template selection; selecting the
  // template clears field selection. Keeps the two mutually exclusive.
  const selectFields = (next) => {
    setTemplateSelected(false)
    setSelection(next)
  }
  const selectTemplate = () => {
    setSelection([])
    setTemplateSelected(true)
  }
  const [marquee, setMarquee] = useState(null) // { startX, startY, endX, endY, shift } in canvas-percent
  const marqueeJustEnded = useRef(false)
  // Drag-to-move state. Shape: { fieldIds: string[], startMouse: {x, y},
  //   startPositions: Map<fieldId, {x, y}>, hasMoved: boolean }. Distinct from the
  //   crop-corner `dragging` state above so the two handlers can coexist.
  const [badgeDrag, setBadgeDrag] = useState(null)
  // Set true on mouseup of a drag that crossed the 3px threshold so the
  // subsequent click event on the badge is suppressed (mirrors marqueeJustEnded).
  const badgeDragJustEnded = useRef(false)
  // Live snap-to-element guides while dragging. `{ x: %|null, y: %|null }`.
  // Cleared on mouseup. Rendered as 1px magenta lines over the canvas.
  const [snapGuides, setSnapGuides] = useState({ x: null, y: null })
  // Live smart-guide / equal-spacing visuals while dragging. Each axis is
  // independent: when matched, holds `{ a, b, c, spacingPct, perp }` where
  // a < b < c are the three center values defining the equal-spacing chain,
  // spacingPct is |b-a| === |c-b| on the matched axis, and perp is the
  // perpendicular-axis canvas-percent at which to draw the segments + pill
  // (for x-axis matches this is the y coord; for y-axis the x coord).
  const [smartGuides, setSmartGuides] = useState({ x: null, y: null })
  // Canvas rendered pixel width, tracked so PlacementBadge can scale
  // design-space (1920-wide) font sizes into on-canvas pixels for a faithful
  // edit-mode text render.
  const [canvasWidth, setCanvasWidth] = useState(0)
  useEffect(() => {
    const el = canvasRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => setCanvasWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const [saving, setSaving] = useState(false)
  const [savedNotice, setSavedNotice] = useState(false)
  // Blob URL of a just-saved upload, kept as the displayed background (the canonical S3 URL
  // 403s from the browser). Owned here, not by the previewUrl cleanup, so it outlives it.
  const savedBlobRef = useRef(null)
  useEffect(() => () => { if (savedBlobRef.current) URL.revokeObjectURL(savedBlobRef.current) }, [])
  // Whether an overlay exists server-side to delete: false once a reset is saved, so a
  // second Save can't delete it again, and true again after the next save.
  const [hasSavedOverlay, setHasSavedOverlay] = useState(!!initialOverlay)
  // `canvasScale` is the ratio of rendered canvas width (px) to the 1920-px
  // design width. Recomputed on ResizeObserver tick.
  const [canvasScale, setCanvasScale] = useState(1)
  // Edit/Preview toggle. 'edit': arrange fields, no backend calls. 'preview':
  // a discrete server render of the current placements — no arranging while
  // active. Never call the preview API while in 'edit'.
  const [editorMode, setEditorMode] = useState('edit')
  const [previewHtml, setPreviewHtml] = useState(null) // self-contained HTML document text for the rendered preview
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState(null)
  const isPreviewMode = editorMode === 'preview'

  // Canonical URL: local preview takes priority over the saved S3 URL. Drives save/preview
  // payloads and every "does a background exist" check — never used as an <img src> or
  // fetch target directly (private bucket).
  const displayUrl = previewUrl ?? savedSvgUrl
  // Browser-facing counterpart: same local-preview priority, but falls back to the
  // presigned read URL instead of the raw canonical one. This is what actually renders.
  const browserSvgUrl = previewUrl ?? savedSvgReadUrl

  // Track canvas column height to sync sidebar
  useEffect(() => {
    const el = canvasColumnRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setSidebarHeight(entry.contentRect.height))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Track canvas display width to keep the pixel-grid overlay aligned with the
  // 1920-px design space when the editor is resized.
  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      if (w > 0) setCanvasScale(w / 1920)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Fetch fields when target mode changes. The default-overlays endpoint doesn't expose
  // a `/fields` route today — the editor's parent route (Task 11) seeds fields once and
  // the variant/targetMode picker is driven by URL params, so skip the refetch here.
  const fetchedTargetMode = useRef(null)
  useEffect(() => {
    if (!supportsTargetMode) return
    if (isDefaultMode) return
    const switched = fetchedTargetMode.current !== null && fetchedTargetMode.current !== targetMode
    fetchedTargetMode.current = targetMode
    getOverlayFields(orgId, seasonId, type, targetMode).then((next) => {
      setFields(next)
      // The modes carry different field sets, so a switch can strand placements the new
      // mode has no field for: they would save, then render nothing. Only on a switch —
      // a saved overlay's placements stay as they are on open.
      if (!switched) return
      const keys = new Set(next.map((f) => f.key))
      setPlacements((prev) => prev.filter((pl) => keys.has(pl.fieldId)))
      setSelection((prev) => prev.filter((id) => keys.has(id)))
    })
  }, [targetMode, supportsTargetMode, orgId, seasonId, type, isDefaultMode])

  // Parse SVG: auto-match named elements to fields (upload-time auto-place). Must fetch
  // via browserSvgUrl (presigned) — the canonical URL 403s from the browser.
  const pendingFileRef = useRef(null)
  pendingFileRef.current = pendingFile
  useEffect(() => {
    if (!browserSvgUrl) return
    let cancelled = false
    const parseSvg = async (url) => {
      try {
        const resp = await fetch(url)
        const text = await resp.text()
        const container = document.createElement('div')
        container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1920px;height:1080px'
        container.innerHTML = text
        document.body.appendChild(container)
        const svg = container.querySelector('svg')
        if (!svg) { container.remove(); return { autoPlace: [] } }
        const vb = svg.getAttribute('viewBox')?.split(/[\s,]+/).map(Number)
        const vw = vb?.[2] || parseFloat(svg.getAttribute('width')) || 1920
        const vh = vb?.[3] || parseFloat(svg.getAttribute('height')) || 1080
        if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${vw} ${vh}`)
        svg.setAttribute('width', vw)
        svg.setAttribute('height', vh)

        const svgPoint = svg.createSVGPoint()
        const getTransformedBBox = (el) => {
          const ctm = el.getCTM()
          if (!ctm) return null
          const bbox = el.getBBox()
          const corners = [
            [bbox.x, bbox.y],
            [bbox.x + bbox.width, bbox.y],
            [bbox.x + bbox.width, bbox.y + bbox.height],
            [bbox.x, bbox.y + bbox.height],
          ].map(([px, py]) => {
            svgPoint.x = px; svgPoint.y = py
            const tp = svgPoint.matrixTransform(ctm)
            return [tp.x, tp.y]
          })
          const xs = corners.map((c) => c[0])
          const ys = corners.map((c) => c[1])
          return {
            x: Math.min(...xs), y: Math.min(...ys),
            w: Math.max(...xs) - Math.min(...xs),
            h: Math.max(...ys) - Math.min(...ys),
          }
        }

        // Auto-match: find elements whose id matches a field key
        const fieldKeys = new Set(fields.map((f) => f.key))
        const autoPlace = []
        svg.querySelectorAll('[id]').forEach((el) => {
          const elId = el.getAttribute('id')
          if (!fieldKeys.has(elId)) return
          const b = getTransformedBBox(el)
          if (!b || b.w < 1 || b.h < 1) return

          // Box-model geometry straight from the element bbox — no legacy
          // point-anchored shape in between (see svgRectToPlacement).
          const fieldDef = fields.find((f) => f.key === elId)
          const placement = svgRectToPlacement(
            fieldDef ?? { key: elId, dataType: 'Text' },
            { x: b.x, y: b.y, width: b.w, height: b.h },
            { w: vw, h: vh },
          )

          if (placement.w == null) {
            // Image field: geometry only, no font/color/anchor to merge.
            autoPlace.push(placement)
            return
          }

          if (fieldDef?.dataType === 'Bar' || fieldDef?.dataType === 'ColorBox') {
            // Bar's hAlign comes from the field key, not the artwork; ColorBox has no anchor at all.
            autoPlace.push(placement)
            return
          }

          // Read font metadata from <text> elements or data-* attributes
          const isText = el.tagName === 'text' || el.tagName === 'tspan'
          const textEl = isText ? el : el.querySelector('text, tspan')
          const cs = textEl ? window.getComputedStyle(textEl) : null

          // Read text-anchor: SVG uses start|middle|end, we use left|center|right.
          // Only overrides the svgRectToPlacement default ('center') when the
          // element actually specifies one.
          const svgAnchor = el.getAttribute('data-text-anchor')
            || textEl?.getAttribute('text-anchor')
            || cs?.textAnchor
            || ''
          const hAlign = svgAnchor === 'start' ? 'left'
            : svgAnchor === 'end' ? 'right'
            : svgAnchor === 'middle' ? 'center'
            : placement.hAlign

          placement.hAlign = hAlign
          placement.fontSize = parseInt(el.getAttribute('data-font-size') || cs?.fontSize, 10) || DEFAULT_FONT_SIZE
          placement.color = el.getAttribute('data-color') || (cs?.fill !== 'none' && cs?.fill) || DEFAULT_COLOR
          autoPlace.push(placement)
        })

        container.remove()
        return { autoPlace }
      } catch { return { autoPlace: [] } }
    }
    parseSvg(browserSvgUrl).then(({ autoPlace }) => {
      if (cancelled) return
      if (autoPlace.length === 0) return
      // A bundled default's artwork names the field areas, so on load we add any field the
      // SVG names that the board has not placed yet. Stored geometry is KEPT — an operator's
      // resize must survive reopening the editor. Newly uploaded artwork is the designer
      // handing over new slots, and only then do the SVG rects win. A season overlay keeps
      // the same rule for the same reason: a client's adjustments are never overwritten.
      if (isDefaultMode) {
        setPlacements((prev) => mergeSvgGeometry(prev, autoPlace, { overrideExisting: !!pendingFileRef.current }))
        return
      }
      if (pendingFileRef.current) {
        setPlacements((prev) => {
          const existing = new Set(prev.map((p) => p.fieldId))
          const newPlacements = autoPlace.filter((a) => !existing.has(a.fieldId))
          return newPlacements.length > 0 ? [...prev, ...newPlacements] : prev
        })
      }
    })
    return () => { cancelled = true }
  }, [browserSvgUrl, fields, isDefaultMode])

  // Revoke object URL when component unmounts or a new file is selected
  useEffect(() => () => { if (previewUrl && previewUrl !== savedBlobRef.current) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  // ── Edit/Preview toggle ────────────────────────────────────────────────
  // Fires a discrete render request to the backend preview endpoint using the
  // CURRENT placements/background/previewText/targetMode. Never invoked while
  // editorMode === 'edit'. Used both on switch-to-preview and on "Refresh
  // preview" while already in preview mode.
  const runPreview = useCallback(async () => {
    if (previewLoading) return // guard against overlapping requests (rapid toggle / refresh spam)
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const background = await resolvePreviewBackground({ pendingFile, displayUrl })
      const body = buildPreviewRequest({
        templateSvgUrl: background,
        placements,
        previewText,
        // Default mode hides the target-mode PICKER but the overlay still HAS a mode, and
        // the preview must resolve the same field set the placements were built against:
        // Starters and Roster are home_/away_ prefixed under Both and unprefixed under
        // Single, so sending null renders the artwork with almost no values in it.
        targetMode: supportsTargetMode || isDefaultMode ? targetMode : null,
        // Current (possibly unsaved) template layout — a preview must match what
        // Edit is showing right now, not just the last-saved layout.
        templateScale,
        templatePosition,
        templateCrop,
        x: templateX,
        y: templateY,
        homeBarColor,
        awayBarColor,
        barColor,
      })
      const html = isDefaultMode
        ? await previewDefaultOverlay(type, body, sport, statsMode)
        : await previewOverlay(orgId, seasonId, type, body)
      setPreviewHtml(html)
    } catch (err) {
      setPreviewError(err?.message ?? 'Failed to render preview')
    } finally {
      setPreviewLoading(false)
    }
  }, [previewLoading, pendingFile, displayUrl, placements, previewText, supportsTargetMode, targetMode, orgId, seasonId, type, mode, isDefaultMode, templateScale, templatePosition, templateCrop, templateX, templateY, homeBarColor, awayBarColor, barColor])

  const handleToggleEditorMode = useCallback((_, next) => {
    if (!next || next === editorMode) return
    if (next === 'edit') {
      setEditorMode('edit')
      setPreviewError(null)
      setPreviewHtml(null)
      return
    }
    // Switching to preview: clear any in-progress arrangement gestures and
    // selection so the toolbar/side-panel transition is clean.
    setPendingField(null)
    selectFields([])
    setEditorMode('preview')
    runPreview()
  }, [editorMode, runPreview])

  // ── Marquee drag-selection ───────────────────────────────────────────
  // While the user holds the mouse on the canvas background and drags, paint a
  // rectangle and on release commit every placement whose visual bounds intersect.
  // Shift held when the marquee finishes means "add to the existing selection";
  // otherwise the selection is replaced. The threshold (1% in either axis)
  // prevents an accidental marquee on a noisy click.
  useEffect(() => {
    if (!marquee) return
    const handleMove = (e) => {
      if (!canvasRef.current) return
      const rect = canvasRef.current.getBoundingClientRect()
      const px = (e.clientX - rect.left) / rect.width * 100
      const py = (e.clientY - rect.top) / rect.height * 100
      setMarquee((prev) => prev ? { ...prev, endX: px, endY: py } : prev)
    }
    const handleUp = () => {
      setMarquee((prev) => {
        if (!prev) return null
        const left = Math.min(prev.startX, prev.endX)
        const right = Math.max(prev.startX, prev.endX)
        const top = Math.min(prev.startY, prev.endY)
        const bottom = Math.max(prev.startY, prev.endY)
        const dragged = (right - left) > 1 || (bottom - top) > 1
        if (dragged) {
          marqueeJustEnded.current = true
          // Order among the marquee's own hits follows placement array order
          // (no inherent click order for a group-drag); shift-add appends them
          // after the existing selection, dedupe keeps each id's LAST position.
          const hits = []
          for (const p of placements) {
            const field = fields.find((f) => f.key === p.fieldId)
            const b = getPlacementBounds(p, field)
            const overlaps = !(b.right < left || b.left > right || b.bottom < top || b.top > bottom)
            if (overlaps) hits.push(p.fieldId)
          }
          selectFields((prevSel) => {
            if (prev.shift) return [...prevSel.filter((id) => !hits.includes(id)), ...hits]
            return hits
          })
        }
        return null
      })
    }
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
  }, [marquee, placements, fields])

  // ── Drag-to-move on a placement badge ────────────────────────────────
  // Records the starting mouse position + each affected placement's starting
  // (x, y). The document-level mousemove effect below converts the pixel delta
  // into canvas-percent and applies it to the whole set in real time. A 3px
  // threshold distinguishes a click from a drag; until it's crossed the badge's
  // onClick still wins. Multi-drag: if the user grabs a badge that's part of an
  // existing multi-selection, every selected placement moves together;
  // otherwise the selection is replaced with just the grabbed one.
  const handleBadgeMouseDown = useCallback((fieldId, event) => {
    if (isPreviewMode) return // no arranging while previewing
    if (event.button !== 0) return // left button only
    if (pendingField) return // canvas-click placement gesture takes priority
    // Multi-drag only when the badge is already part of an active multi-selection.
    // Otherwise the drag affects just this badge; selection is left untouched so
    // the click handler (which fires on a no-drag mouseup) keeps its existing
    // toggle/replace semantics intact.
    const willMove = selection.includes(fieldId) && selection.length > 1
      ? [...selection]
      : [fieldId]
    const startPositions = new Map()
    for (const id of willMove) {
      const p = placements.find((pl) => pl.fieldId === id)
      if (p) startPositions.set(id, { x: p.x, y: p.y })
    }
    setBadgeDrag({
      fieldIds: willMove,
      startMouse: { x: event.clientX, y: event.clientY },
      startPositions,
      hasMoved: false,
    })
  }, [selection, placements, pendingField, isPreviewMode])

  useEffect(() => {
    if (!badgeDrag) return
    const clamp = (v) => Math.max(0, Math.min(100, v))
    // Snap threshold = 4 canvas-pixels (≈ 0.21% x, ≈ 0.37% y). Held Alt
    // bypasses element snap; grid snap (when enabled) wins on tie because
    // the element-snap step reads the post-grid candidate position.
    const snapThresholdX = (4 / 1920) * 100
    const snapThresholdY = (4 / 1080) * 100
    const handleMove = (e) => {
      if (!canvasRef.current) return
      const dxPx = e.clientX - badgeDrag.startMouse.x
      const dyPx = e.clientY - badgeDrag.startMouse.y
      if (!badgeDrag.hasMoved && Math.hypot(dxPx, dyPx) < 3) return
      if (!badgeDrag.hasMoved) {
        setBadgeDrag((prev) => prev ? { ...prev, hasMoved: true } : prev)
      }
      const rect = canvasRef.current.getBoundingClientRect()
      const dxPct = (dxPx / rect.width) * 100
      const dyPct = (dyPx / rect.height) * 100

      // Compute a snap-correction (snapDx, snapDy) once per mousemove based on
      // the first dragged placement's candidate (post-grid) position. The same
      // correction is applied to all dragged placements so their relative
      // offsets are preserved. Bounds of non-dragged placements come from their
      // current x/y. Alt held bypasses element snap.
      const draggedIds = new Set(badgeDrag.fieldIds)
      const draggedSet = placements.filter((p) => draggedIds.has(p.fieldId))
      const primary = draggedSet[0]
      const primaryStart = primary ? badgeDrag.startPositions.get(primary.fieldId) : null
      let snapDx = 0
      let snapDy = 0
      let guideX = null
      let guideY = null
      let smartX = null
      let smartY = null
      if (!e.altKey && primary && primaryStart) {
        const primaryField = fields.find((f) => f.key === primary.fieldId)
        // Candidate position before element-snap: drag-delta then clamp.
        const candX = clamp(primaryStart.x + dxPct)
        const candY = clamp(primaryStart.y + dyPct)
        const draggedBounds = getSnapBounds(primary, primaryField, candX, candY)
        const otherBoundsByField = []
        for (const op of placements) {
          if (draggedIds.has(op.fieldId)) continue
          const of = fields.find((f) => f.key === op.fieldId)
          otherBoundsByField.push(getSnapBounds(op, of, op.x, op.y))
        }
        const snap = findSnapTargets(draggedBounds, otherBoundsByField, snapThresholdX, snapThresholdY)
        if (snap.deltaX !== null) {
          snapDx = snap.deltaX
          guideX = snap.guideX
        }
        if (snap.deltaY !== null) {
          snapDy = snap.deltaY
          guideY = snap.guideY
        }

        // Smart guides — equal-spacing detection. Independent per axis.
        // Skip an axis element-snap already claimed (the two features compete for
        // the same "near another element" intent, and edge-snap wins).
        // For the matched axis, render guide segments + a px spacing pill.
        if (guideX === null) {
          const draggedCx = draggedBounds.centerX + snapDx
          const otherCx = otherBoundsByField.map((b) => b.centerX)
          const sg = findSmartGuide(draggedCx, otherCx, snapThresholdX)
          if (sg) {
            snapDx += sg.delta
            // Use the dragged placement's centerY as the horizontal line for the
            // guide segments / pill. Median of all other centerYs would also work,
            // but anchoring on the dragged element keeps the pill near the user's
            // cursor.
            smartX = {
              a: sg.a,
              b: sg.b,
              c: sg.c,
              spacingPct: sg.spacing,
              perp: draggedBounds.centerY + snapDy,
            }
          }
        }
        if (guideY === null) {
          const draggedCy = draggedBounds.centerY + snapDy
          const otherCy = otherBoundsByField.map((b) => b.centerY)
          const sg = findSmartGuide(draggedCy, otherCy, snapThresholdY)
          if (sg) {
            snapDy += sg.delta
            smartY = {
              a: sg.a,
              b: sg.b,
              c: sg.c,
              spacingPct: sg.spacing,
              perp: draggedBounds.centerX + snapDx,
            }
          }
        }
      }
      setSnapGuides((prev) => (prev.x === guideX && prev.y === guideY ? prev : { x: guideX, y: guideY }))
      setSmartGuides((prev) => (prev.x === smartX && prev.y === smartY ? prev : { x: smartX, y: smartY }))

      setPlacements((prev) => prev.map((p) => {
        const start = badgeDrag.startPositions.get(p.fieldId)
        if (!start) return p
        const nx = clamp(start.x + dxPct + snapDx)
        const ny = clamp(start.y + dyPct + snapDy)
        return {
          ...p,
          x: +nx.toFixed(2),
          y: +ny.toFixed(2),
        }
      }))
    }
    const handleUp = () => {
      if (badgeDrag.hasMoved) badgeDragJustEnded.current = true
      setBadgeDrag(null)
      setSnapGuides({ x: null, y: null })
      setSmartGuides({ x: null, y: null })
    }
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
  }, [badgeDrag, placements, fields])

  const handleCanvasMouseDown = useCallback((e) => {
    if (isPreviewMode) return // no arranging while previewing
    // Don't marquee while placing a field — that gesture is "click to place".
    if (pendingField) return
    // Only start when the mousedown originated on the canvas background itself,
    // not bubbled up from a badge or SVG rect.
    if (e.target !== canvasRef.current) return
    if (!canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const px = (e.clientX - rect.left) / rect.width * 100
    const py = (e.clientY - rect.top) / rect.height * 100
    setMarquee({ startX: px, startY: py, endX: px, endY: py, shift: e.shiftKey })
  }, [pendingField, isPreviewMode])

  // Fades the placement hint pill when the cursor is over/near it (it's
  // pointer-events:none so CSS :hover never fires on it — compute from
  // cursor coords vs. its measured rect instead).
  const handleCanvasMouseMove = useCallback((e) => {
    if (!pendingField || !hintRef.current) return
    const r = hintRef.current.getBoundingClientRect()
    const margin = 24
    const near = e.clientX >= r.left - margin && e.clientX <= r.right + margin
      && e.clientY >= r.top - margin && e.clientY <= r.bottom + margin
    setHintFaded((prev) => (prev === near ? prev : near))
  }, [pendingField])

  useEffect(() => { if (!pendingField) setHintFaded(false) }, [pendingField])

  // ── Crop dot dragging ────────────────────────────────────────────────
  useEffect(() => {
    if (!dragging) return
    const handleMove = (e) => {
      if (!svgWrapperRef.current) return
      const rect = svgWrapperRef.current.getBoundingClientRect()
      const px = (e.clientX - rect.left) / rect.width * 100
      const py = (e.clientY - rect.top) / rect.height * 100
      const clamp = (v) => Math.max(0, Math.min(50, Math.round(v)))

      setTemplateCrop((prev) => {
        const next = { ...prev }
        if (dragging.v === 'top')    next.top    = clamp(py)
        if (dragging.v === 'bottom') next.bottom = clamp(100 - py)
        if (dragging.h === 'left')   next.left   = clamp(px)
        if (dragging.h === 'right')  next.right  = clamp(100 - px)
        return next
      })
    }
    const handleUp = () => setDragging(null)
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
  }, [dragging])

  // ── Whole-template drag (free placement) ───────────────────────────────
  // Grabbing the template image drags the whole box anywhere on the canvas,
  // setting templateX/templateY (percent of canvas, top-left of the box) —
  // same box-model convention as a field placement. The drag start reads the
  // box's CURRENT on-screen position via getBoundingClientRect (rather than
  // computing it from templatePosition/crop), so it works identically whether
  // the box is presently anchor-positioned or already free-positioned, with
  // no jump at drag start.
  const handleTemplateMouseDown = useCallback((e) => {
    if (isPreviewMode) return
    if (pendingField) return // canvas-click placement gesture takes priority
    if (e.button !== 0) return
    if (!canvasRef.current || !svgWrapperRef.current) return
    e.preventDefault()
    e.stopPropagation()
    selectTemplate()
    const canvasRect = canvasRef.current.getBoundingClientRect()
    const wrapperRect = svgWrapperRef.current.getBoundingClientRect()
    setTemplateDrag({
      startMouse: { x: e.clientX, y: e.clientY },
      startX: (wrapperRect.left - canvasRect.left) / canvasRect.width * 100,
      startY: (wrapperRect.top - canvasRect.top) / canvasRect.height * 100,
    })
  }, [isPreviewMode, pendingField])

  useEffect(() => {
    if (!templateDrag) return
    const handleMove = (e) => {
      if (!canvasRef.current) return
      const rect = canvasRef.current.getBoundingClientRect()
      const dxPct = (e.clientX - templateDrag.startMouse.x) / rect.width * 100
      const dyPct = (e.clientY - templateDrag.startMouse.y) / rect.height * 100
      setTemplateX(+(templateDrag.startX + dxPct).toFixed(2))
      setTemplateY(+(templateDrag.startY + dyPct).toFixed(2))
    }
    const handleUp = () => setTemplateDrag(null)
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
  }, [templateDrag])

  // ── File selected: show preview locally, don't upload yet ─────────────
  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(file) })
    setPendingFile(file)
  }, [])

  // Commit a pending field onto the canvas at (x, y) as a fresh box-model
  // placement. (x, y) here is the click point — the box's top-left is derived
  // so the box is centered on that point.
  const commitPlacement = (clickX, clickY) => {
    const placement = newFieldPlacement(pendingField, { x: clickX, y: clickY })
    if (pendingField.dataType === 'Image') {
      placement.x = +(clickX - placement.imageWidth / 2).toFixed(2)
      placement.y = +(clickY - placement.imageHeight / 2).toFixed(2)
      return placement
    }
    if (pendingField.dataType === 'Bar') {
      // x is an anchor edge, not a corner — centre the track on the click
      // point along its fill axis; y is already the vertical centre.
      placement.x = +(placement.hAlign === 'right' ? clickX + placement.w / 2
        : placement.hAlign === 'left' ? clickX - placement.w / 2
        : clickX).toFixed(2)
      placement.y = clickY
      return placement
    }
    placement.x = +(clickX - placement.w / 2).toFixed(2)
    placement.y = +(clickY - placement.h / 2).toFixed(2)
    return placement
  }

  // ── Click canvas to place a pending field, centered on the cursor ─────
  const handleCanvasClick = useCallback((e) => {
    if (isPreviewMode) return // no arranging while previewing
    // Suppress the click that follows a marquee drag (mouseup commits the marquee
    // but the browser still fires a click event right after).
    if (marqueeJustEnded.current) {
      marqueeJustEnded.current = false
      return
    }
    if (pendingField && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const x = +((e.clientX - rect.left) / rect.width * 100).toFixed(2)
      const y = +((e.clientY - rect.top) / rect.height * 100).toFixed(2)
      setPlacements((prev) => [...prev.filter((p) => p.fieldId !== pendingField.key), commitPlacement(x, y)])
      setPendingField(null)
      selectFields([pendingField.key])
      return
    }
    // Plain click on canvas background clears the selection (fields or template).
    if (!pendingField && (selection.length > 0 || templateSelected)) selectFields([])
  }, [pendingField, selection, isPreviewMode, templateSelected])

  const updatePlacement = (fieldId, patch) =>
    setPlacements((prev) => prev.map((p) => p.fieldId === fieldId ? { ...p, ...patch } : p))

  // Applies fn(placement) -> patch to each listed placement.
  const updatePlacements = (ids, fn) =>
    setPlacements((prev) => prev.map((p) => ids.includes(p.fieldId) ? { ...p, ...fn(p) } : p))

  const removePlacements = (ids) => {
    setPlacements((prev) => prev.filter((p) => !ids.includes(p.fieldId)))
    selectFields([])
  }

  const removePlacement = (fieldId) => {
    setPlacements((prev) => prev.filter((p) => p.fieldId !== fieldId))
    selectFields((prev) => (prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : prev))
  }

  // Keyboard shortcuts while one or more placements are selected:
  //   Backspace/Delete           remove selected
  //   Arrow keys                 nudge 1 canvas-px (≈ 0.0521% x / 0.0926% y)
  //   Shift + Arrow              10 px
  //   Ctrl/Cmd + Arrow           0.1 px (sub-pixel kerning)
  //   Ctrl/Cmd + D               duplicate selected with +1px offset
  // Typing in an INPUT/TEXTAREA/SELECT (or contentEditable) suppresses the
  // shortcut so the right-panel steppers keep working.
  useEffect(() => {
    if (selection.length === 0) return
    if (isPreviewMode) return // no arranging while previewing
    const clamp = (v) => Math.max(0, Math.min(100, v))
    const round2 = (v) => +v.toFixed(2)
    const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
    const handleKey = (e) => {
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault()
        setPlacements((prev) => prev.filter((p) => !selection.includes(p.fieldId)))
        selectFields([])
        return
      }

      if (ARROW_KEYS.includes(e.key)) {
        e.preventDefault()
        const step = e.shiftKey ? 10 : (e.metaKey || e.ctrlKey) ? 0.1 : 1
        const dxPx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
        const dyPx = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
        const dxPct = dxPx * (100 / 1920)
        const dyPct = dyPx * (100 / 1080)
        setPlacements((prev) => prev.map((p) => {
          if (!selection.includes(p.fieldId)) return p
          return { ...p, x: round2(clamp(p.x + dxPct)), y: round2(clamp(p.y + dyPct)) }
        }))
        return
      }

      if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        // Placements are uniquely keyed by fieldId (see commitPlacement's caller in
        // handleCanvasClick, which filters out any pre-existing placement with the
        // same key). To duplicate without collisions, generate a fresh id by appending _copy,
        // _copy2, _copy3, ... walking until we find one that's not already used.
        const newIdByOldId = new Map()
        setPlacements((prev) => {
          const taken = new Set(prev.map((p) => p.fieldId))
          const newOnes = []
          const nextId = (base) => {
            const stripped = base.replace(/_copy\d*$/, '')
            for (let i = 1; i < 10000; i += 1) {
              const candidate = i === 1 ? `${stripped}_copy` : `${stripped}_copy${i}`
              if (!taken.has(candidate)) {
                taken.add(candidate)
                return candidate
              }
            }
            // Pathological fallback — 10k copies of the same field is absurd, but
            // return a unique fieldId rather than undefined so React keys stay valid.
            return `${stripped}_copy_${Date.now()}`
          }
          for (const p of prev) {
            if (!selection.includes(p.fieldId)) continue
            const dxPct = 1 * (100 / 1920)
            const dyPct = 1 * (100 / 1080)
            const newId = nextId(p.fieldId)
            newIdByOldId.set(p.fieldId, newId)
            newOnes.push({
              ...p,
              fieldId: newId,
              x: round2(clamp(p.x + dxPct)),
              y: round2(clamp(p.y + dyPct)),
            })
          }
          return [...prev, ...newOnes]
        })
        // Select the newly-created copies, preserving the prior click order
        // (so the prior anchor's copy is still last / still the anchor).
        selectFields((prev) => prev.map((id) => newIdByOldId.get(id)).filter(Boolean))
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [selection, placements, isPreviewMode])

  // Esc cancels an in-progress field placement.
  useEffect(() => {
    if (!pendingField) return
    const handleKey = (e) => {
      if (e.key !== 'Escape') return
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      setPendingField(null)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [pendingField])

  // ── Save: upload file if new, then persist placements ─────────────────
  // Default mode never deletes — the bundled SVG always exists on disk and "reset" isn't
  // a concept there. Season mode treats an empty displayUrl as a delete (the user clicked
  // "Reset to Default" to remove their custom overlay).
  const isReset = !displayUrl && !isDefaultMode
  // An overlay requires a background — the backend rejects a background-less save.
  // The only background-less Save allowed is committing a Reset to Default (a
  // delete) of an overlay that exists server-side.
  const canSave = !!displayUrl || (isReset && hasSavedOverlay)
  // Re-baselines the dirty-check snapshot; call only after a save actually succeeds.
  const refreshOpenedSnapshot = (resolvedSvgUrl) => {
    openedSnapshotRef.current = {
      placements,
      templateScale,
      templatePosition,
      templateCrop,
      templateX,
      templateY,
      homeBarColor,
      awayBarColor,
      barColor,
      savedSvgUrl: resolvedSvgUrl,
    }
  }
  const handleSave = async () => {
    setSaving(true)
    try {
      if (isReset) {
        await dataSource.remove()
        setHasSavedOverlay(false)
        refreshOpenedSnapshot(savedSvgUrl)
      } else {
        const { savedSvgUrl: newSvgUrl } = await dataSource.save({
          pendingFile,
          savedSvgUrl,
          placements,
          templateScale,
          templatePosition,
          templateCrop,
          x: templateX,
          y: templateY,
          homeBarColor,
          awayBarColor,
          barColor,
          targetMode,
          supportsTargetMode,
        })
        // Default mode saves the SVG text itself, so the uploaded file becomes the saved
        // background; season mode keeps the canonical S3 URL the backend stored.
        const nextSavedUrl = pendingFile && isDefaultMode ? previewUrl : (newSvgUrl ?? savedSvgUrl)
        if (pendingFile) {
          if (savedBlobRef.current) URL.revokeObjectURL(savedBlobRef.current)
          savedBlobRef.current = previewUrl
          setSavedSvgUrl(nextSavedUrl)
          setSavedSvgReadUrl(previewUrl)
          setPendingFile(null)
          setPreviewUrl(null)
        }
        setHasSavedOverlay(true)
        refreshOpenedSnapshot(nextSavedUrl)
      }
      setSavedNotice(true)
      await onSave?.()
    } finally {
      setSaving(false)
    }
  }

  const handleResetToDefault = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    if (savedBlobRef.current) URL.revokeObjectURL(savedBlobRef.current)
    savedBlobRef.current = null
    setPreviewUrl(null)
    setPendingFile(null)
    setSavedSvgUrl(null)
    setSavedSvgReadUrl(null)
    setPlacements([])
    setTemplateScale(100)
    setTemplatePosition('center')
    setTemplateCrop(DEFAULT_CROP)
    setTemplateX(null)
    setTemplateY(null)
    setHomeBarColor('team')
    setAwayBarColor('team')
    setBarColor('team')
    setTemplateSelected(false)
  }

  const placedIds = new Set(placements.map((p) => p.fieldId))
  const hasCrop = !!(templateCrop.top || templateCrop.right || templateCrop.bottom || templateCrop.left)
  const barFields = fields.filter((f) => f.dataType === 'Bar')

  // True if there's a pending background, or placements/layout diverge from openedSnapshotRef.
  const isDirty = () => {
    if (pendingFile) return true
    const opened = openedSnapshotRef.current
    return JSON.stringify(placements) !== JSON.stringify(opened.placements)
      || JSON.stringify(templateCrop) !== JSON.stringify(opened.templateCrop)
      || templateScale !== opened.templateScale
      || templatePosition !== opened.templatePosition
      || templateX !== opened.templateX
      || templateY !== opened.templateY
      || homeBarColor !== opened.homeBarColor
      || awayBarColor !== opened.awayBarColor
      || barColor !== opened.barColor
      || savedSvgUrl !== opened.savedSvgUrl
  }

  const handleVariantSwitch = (nextVariant) => {
    if (nextVariant === variant) return
    if (isDirty() && !window.confirm('You have unsaved changes. Discard them and switch sides?')) return
    onVariantChange?.(nextVariant)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid #e0e0e0' }}>
        <Button onClick={onCancel} size='small'>← Back</Button>
        <Typography variant='h6' style={{ flex: 1 }}>
          {variant ? `${labelFor(type)} (${variantLabelForSlug(SLUGS[type] ?? type, variant)})` : labelFor(type)}
        </Typography>
        {hasVariantSelector && (
          <ToggleButtonGroup
            value={variant}
            exclusive
            onChange={(_, v) => { if (v !== null) handleVariantSwitch(v) }}
            size='small'
          >
            {variantOptions.map((v) => (
              <ToggleButton key={v} value={v} style={{ fontSize: 11, padding: '4px 10px', textTransform: 'none' }}>
                {variantLabelForSlug(SLUGS[type] ?? type, v)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {displayUrl && !isDefaultMode && (
          <Button
            size='small'
            onClick={handleResetToDefault}
          >
            Reset to default
          </Button>
        )}
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </div>

      <div style={{ display: 'flex' }}>
        {/* Left: field palette */}
        <FieldPalette
          fields={fields}
          placedIds={placedIds}
          activeKey={pendingField?.key}
          selectedKeys={selection}
          disabled={isPreviewMode || !displayUrl}
          disabledReason={!displayUrl ? 'Upload a background first' : null}
          search={fieldSearch}
          onSearchChange={setFieldSearch}
          height={sidebarHeight}
          onPick={(f, { additive }) => {
            if (placedIds.has(f.key)) {
              setPendingField(null)
              if (!additive) selectFields([f.key])
              else if (selection.includes(f.key)) selectFields(selection.filter((id) => id !== f.key))
              else selectFields(addToSelection(selection, f.key))
              return
            }
            setPendingField(pendingField?.key === f.key ? null : f)
            selectFields([])
          }}
          onSelectMany={(list) => {
            const keys = list.filter((f) => placedIds.has(f.key)).map((f) => f.key)
            if (keys.length === 0) return
            setPendingField(null)
            selectFields(keys)
          }}
          onRemove={(f) => removePlacement(f.key)}
        >
          {supportsTargetMode && !isDefaultMode && (
            <ToggleButtonGroup
              value={targetMode}
              exclusive
              onChange={(_, v) => { if (v) setTargetMode(v) }}
              size='small'
              style={{ width: '100%' }}
            >
              <ToggleButton value='Single' style={{ flex: 1, fontSize: 12, padding: '4px 8px', textTransform: 'none' }}>
                One team
              </ToggleButton>
              <ToggleButton value='Both' style={{ flex: 1, fontSize: 12, padding: '4px 8px', textTransform: 'none' }}>
                Both teams
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </FieldPalette>

        {/* Center: canvas */}
        <div ref={canvasColumnRef} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Toolbar: upload + layout controls */}
          <div style={{ padding: '6px 12px', borderBottom: '1px solid #e0e0e0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <ToggleButtonGroup
              value={editorMode}
              exclusive
              onChange={handleToggleEditorMode}
              size='small'
            >
              <ToggleButton value='edit' style={{ fontSize: 11, padding: '4px 10px', textTransform: 'none' }}>
                Edit
              </ToggleButton>
              <ToggleButton value='preview' style={{ fontSize: 11, padding: '4px 10px', textTransform: 'none' }} disabled={!displayUrl || previewLoading}>
                Preview
              </ToggleButton>
            </ToggleButtonGroup>
            <Button variant='outlined' size='small' component='label' disabled={isPreviewMode}>
              {displayUrl ? 'Change background' : 'Upload background'}
              <input type='file' accept='.svg' hidden onChange={handleFileChange} disabled={isPreviewMode} />
            </Button>
            {displayUrl && (
              <Tooltip title='Select the background template' placement='top'>
                <span>
                  <Button
                    variant={templateSelected ? 'contained' : 'outlined'}
                    color={templateSelected ? 'primary' : 'default'}
                    size='small'
                    disabled={isPreviewMode}
                    onClick={selectTemplate}
                    style={{ fontSize: 11, padding: '2px 10px', minWidth: 'auto' }}
                  >
                    Background
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* Template-layout controls (scale, position, crop) only apply to season-scoped
                overlays; the default-overlays API doesn't carry these fields, so hide them in
                default mode. Also only usable while the template is selected — greyed
                (not hidden) otherwise, same as while previewing. */}
            {displayUrl && !isDefaultMode && (() => {
              const templateControlsEnabled = templateSelected && !isPreviewMode
              return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: templateControlsEnabled ? 1 : 0.4, pointerEvents: templateControlsEnabled ? 'auto' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 140 }}>
                  <Typography variant='caption' color='textSecondary' style={{ whiteSpace: 'nowrap' }}>
                    Scale: {templateScale}%
                  </Typography>
                  <Slider
                    value={templateScale}
                    onChange={(_, v) => setTemplateScale(v)}
                    min={20}
                    max={100}
                    step={5}
                    size='small'
                    disabled={!templateControlsEnabled}
                    style={{ minWidth: 80 }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  {POSITIONS.flat().map((pos) => {
                    // A free-dragged position (x/y set) overrides the anchor entirely, so
                    // no preset reads as "active" while one is in effect.
                    const active = templateX == null && templateY == null && templatePosition === pos
                    return (
                      <Tooltip key={pos} title={`${pos} (clears free position)`} placement='top'>
                        <div
                          onClick={() => {
                            setTemplatePosition(pos)
                            setTemplateX(null)
                            setTemplateY(null)
                          }}
                          style={{
                            width: 18,
                            height: 12,
                            borderRadius: 2,
                            border: `1.5px solid ${active ? '#1976d2' : '#ccc'}`,
                            background: active ? '#1976d2' : '#f5f5f5',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    )
                  })}
                </div>
                {hasCrop && (
                  <Button
                    size='small'
                    onClick={() => setTemplateCrop(DEFAULT_CROP)}
                    style={{ fontSize: 11, minWidth: 'auto', padding: '2px 8px' }}
                  >
                    Reset crop
                  </Button>
                )}
              </div>
              )
            })()}
            {/* Bar fill colour: a board setting, not a per-placement one — shown once the
                board has any Bar field. Team Comparison shows both sides; every other
                board shows one. */}
            {!isDefaultMode && barFields.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {type === 'TeamComparison' ? (
                  <>
                    <BarColorPicker label='Home bar color' value={homeBarColor} onChange={setHomeBarColor} />
                    <BarColorPicker label='Away bar color' value={awayBarColor} onChange={setAwayBarColor} />
                  </>
                ) : (
                  <BarColorPicker label='Bar color' value={barColor} onChange={setBarColor} />
                )}
              </div>
            )}
            <div style={{ flex: 1 }} />
            {!isPreviewMode && selection.length >= 2 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginRight: 8 }}>
                <Typography variant='caption' color='textSecondary' style={{ marginRight: 4 }}>
                  Align
                </Typography>
                <Tooltip title='Align left to last selected' placement='top'>
                  <IconButton size='small' onClick={() => setPlacements(alignToAnchor(placements, selection, 'left'))} style={{ padding: 4 }}>
                    <FormatAlignLeftIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Center horizontally to last selected' placement='top'>
                  <IconButton size='small' onClick={() => setPlacements(alignToAnchor(placements, selection, 'centerH'))} style={{ padding: 4 }}>
                    <FormatAlignCenterIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Align right to last selected' placement='top'>
                  <IconButton size='small' onClick={() => setPlacements(alignToAnchor(placements, selection, 'right'))} style={{ padding: 4 }}>
                    <FormatAlignRightIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Align top to last selected' placement='top'>
                  <IconButton size='small' onClick={() => setPlacements(alignToAnchor(placements, selection, 'top'))} style={{ padding: 4 }}>
                    <VerticalAlignTopIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Center vertically to last selected' placement='top'>
                  <IconButton size='small' onClick={() => setPlacements(alignToAnchor(placements, selection, 'centerV'))} style={{ padding: 4 }}>
                    <VerticalAlignCenterIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Align bottom to last selected' placement='top'>
                  <IconButton size='small' onClick={() => setPlacements(alignToAnchor(placements, selection, 'bottom'))} style={{ padding: 4 }}>
                    <VerticalAlignBottomIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {selection.length >= 3 && (
                  <>
                    <div style={{ width: 1, height: 18, background: '#e0e0e0', margin: '0 4px' }} />
                    <Tooltip title='Distribute horizontally' placement='top'>
                      <IconButton size='small' onClick={() => setPlacements(distributeHorizontal(placements, new Set(selection)))} style={{ padding: 4 }}>
                        <FormatLineSpacingIcon style={{ fontSize: 18, transform: 'rotate(90deg)' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Distribute vertically' placement='top'>
                      <IconButton size='small' onClick={() => setPlacements(distributeVertical(placements, new Set(selection)))} style={{ padding: 4 }}>
                        <FormatLineSpacingIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            )}
          </div>

          {/* SVG canvas */}
          <div>
          <div
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            style={{
              width: '100%',
              paddingTop: '56.25%',
              position: 'relative',
              cursor: pendingField ? 'crosshair' : 'default',
              ...checkerboard(16),
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {pendingField && (
              <div
                ref={hintRef}
                style={{
                  position: 'absolute',
                  top: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(25, 118, 210, 0.75)',
                  color: '#fff',
                  padding: '6px 14px',
                  borderRadius: 16,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 40,
                  opacity: hintFaded ? 0.15 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <Typography variant='caption' style={{ color: 'inherit' }}>
                  Click on the canvas to place &quot;{pendingField.label}&quot; — Esc to cancel
                </Typography>
              </div>
            )}
            {isPreviewMode ? (
              <div style={{ position: 'absolute', inset: 0 }}>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}
                >
                  {previewLoading && <CircularProgress size={32} />}
                  {!previewLoading && previewError && (
                    <Typography variant='body2' color='error' style={{ padding: 16, textAlign: 'center' }}>
                      {previewError}
                    </Typography>
                  )}
                </div>
                {!previewLoading && !previewError && previewHtml && (
                  <iframe
                    title='overlay preview'
                    srcDoc={previewHtml}
                    sandbox='allow-scripts'
                    style={{
                      position: 'absolute', top: 0, left: 0,
                      width: 1920, height: 1080,
                      transform: `scale(${canvasScale})`,
                      transformOrigin: 'top left',
                      border: 'none', display: 'block',
                    }}
                  />
                )}
              </div>
            ) : (
              <>
            {displayUrl ? (
              <div
                ref={svgWrapperRef}
                style={{
                  ...positionStyle(templatePosition, templateScale, templateCrop, templateX, templateY),
                  pointerEvents: 'none',
                  // Selection outline. `outline` (not `border`) so it never disturbs the
                  // box's own layout/sizing that the crop math above depends on.
                  outline: templateSelected ? '2px solid #1976d2' : 'none',
                  outlineOffset: -2,
                }}
              >
                {/* Ghost: uncropped at low opacity */}
                <img
                  src={browserSvgUrl}
                  alt=''
                  style={{ width: '100%', display: 'block', opacity: hasCrop ? 0.25 : 0, pointerEvents: 'none' }}
                />
                {/* Visible: cropped. Draggable — grabbing it moves the whole template box
                    (free placement), setting templateX/templateY. Mousedown also selects
                    the template (never a resize — size only changes via the scale slider).
                    Click stopPropagation so it doesn't bubble to the canvas's plain-click
                    deselect handler right after selecting. Non-interactive while a field
                    is pending — placement mode must win over template select/drag. */}
                <img
                  src={browserSvgUrl}
                  alt='overlay template'
                  onMouseDown={handleTemplateMouseDown}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    display: 'block',
                    pointerEvents: pendingField ? 'none' : 'auto',
                    cursor: pendingField ? 'crosshair' : 'move',
                    ...(hasCrop && { clipPath: `inset(${templateCrop.top}% ${templateCrop.right}% ${templateCrop.bottom}% ${templateCrop.left}%)` }),
                  }}
                />
                {/* Crop boundary */}
                <div style={{
                  position: 'absolute',
                  top: `${templateCrop.top}%`,
                  left: `${templateCrop.left}%`,
                  right: `${templateCrop.right}%`,
                  bottom: `${templateCrop.bottom}%`,
                  border: '1px dashed rgba(255,255,255,0.6)',
                  pointerEvents: 'none',
                }} />
                {/* Corner dots — crop controls, usable only while the template is
                    selected and no field placement is pending. */}
                {CORNERS.map((corner) => (
                  <div
                    key={corner.id}
                    onMouseDown={(e) => {
                      if (!templateSelected || pendingField) return
                      e.preventDefault(); e.stopPropagation(); setDragging(corner)
                    }}
                    style={{
                      position: 'absolute',
                      top: `${corner.v === 'top' ? templateCrop.top : 100 - templateCrop.bottom}%`,
                      left: `${corner.h === 'left' ? templateCrop.left : 100 - templateCrop.right}%`,
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#fff',
                      border: '2px solid #1976d2',
                      transform: 'translate(-50%, -50%)',
                      cursor: templateSelected ? corner.cursor : 'default',
                      opacity: templateSelected ? 1 : 0.35,
                      pointerEvents: (templateSelected && !pendingField) ? 'auto' : 'none',
                      zIndex: 2,
                    }}
                  />
                ))}
              </div>
            ) : null}

            {/* Placement mode wins over every other canvas interaction: while a field is
                pending, existing badges must not swallow the click meant to place it. */}
            <div style={{ pointerEvents: pendingField ? 'none' : 'auto' }}>
            {placements.map((p) => (
              <PlacementBadge
                key={p.fieldId}
                placement={p}
                field={fields.find((f) => f.key === p.fieldId)}
                isEditing={selection.includes(p.fieldId)}
                onMouseDown={(e) => handleBadgeMouseDown(p.fieldId, e)}
                onResize={updatePlacement}
                onClick={(e) => {
                  // Suppress the click that follows a drag (mouseup fires before
                  // click, so the ref is set just in time).
                  if (badgeDragJustEnded.current) {
                    badgeDragJustEnded.current = false
                    return
                  }
                  const additive = e?.shiftKey || e?.metaKey || e?.ctrlKey
                  selectFields((prev) => {
                    if (additive) {
                      // Shift/Ctrl-click: toggle membership. Adding moves the
                      // clicked id to the END (it becomes the new anchor for
                      // align-to-last-selected); removing just drops it.
                      if (prev.includes(p.fieldId)) return prev.filter((id) => id !== p.fieldId)
                      return addToSelection(prev, p.fieldId)
                    }
                    // Plain click: if already the sole selection, toggle off (preserves
                    // the prior "click selected badge to deselect" behavior); otherwise
                    // replace selection with this single placement.
                    if (prev.length === 1 && prev[0] === p.fieldId) return []
                    return [p.fieldId]
                  })
                }}
                text={displayValue(previewText[p.fieldId] || fields.find((f) => f.key === p.fieldId)?.sampleValue || '', p)}
                scale={canvasWidth / 1920}
                aspectLocked={isAspectLocked(p.fieldId)}
              />
            ))}
            </div>
            {/* Snap-to-element guides while dragging: 1px magenta lines at
                the snap target. x guide is vertical, y guide is horizontal.
                Rendered above placements so the user can see them; pointer
                events disabled so they don't intercept the drag. */}
            {badgeDrag && badgeDrag.hasMoved && snapGuides.x !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${snapGuides.x}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: '#ff00ff',
                  pointerEvents: 'none',
                  zIndex: 30,
                }}
              />
            )}
            {badgeDrag && badgeDrag.hasMoved && snapGuides.y !== null && (
              <div
                style={{
                  position: 'absolute',
                  top: `${snapGuides.y}%`,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: '#ff00ff',
                  pointerEvents: 'none',
                  zIndex: 30,
                }}
              />
            )}
            {/* Smart guides: equal-spacing visuals while dragging. For an x-axis
                match, draw two horizontal segments along `perp` (canvas-percent y)
                connecting a↔b and b↔c, plus a pill labeled with the px spacing.
                For a y-axis match, draw two vertical segments along `perp` (x).
                Pill is centered between b and c. Pointer events disabled so the
                drag continues normally. */}
            {badgeDrag && badgeDrag.hasMoved && smartGuides.x && (() => {
              const { a, b, c, spacingPct, perp } = smartGuides.x
              const spacingPx = Math.round((spacingPct / 100) * 1920)
              const pillCenterPct = (b + c) / 2
              return (
                <React.Fragment>
                  {/* Segment a ↔ b */}
                  <div
                    style={{
                      position: 'absolute',
                      top: `${perp}%`,
                      left: `${a}%`,
                      width: `${b - a}%`,
                      height: 1,
                      background: '#ff00ff',
                      pointerEvents: 'none',
                      zIndex: 30,
                    }}
                  />
                  {/* Segment b ↔ c */}
                  <div
                    style={{
                      position: 'absolute',
                      top: `${perp}%`,
                      left: `${b}%`,
                      width: `${c - b}%`,
                      height: 1,
                      background: '#ff00ff',
                      pointerEvents: 'none',
                      zIndex: 30,
                    }}
                  />
                  {/* Tick marks at a, b, c so the chain reads as three anchored
                      points rather than two lines that happen to abut. */}
                  {[a, b, c].map((v) => (
                    <div
                      key={`smart-x-tick-${v}`}
                      style={{
                        position: 'absolute',
                        top: `${perp}%`,
                        left: `${v}%`,
                        width: 1,
                        height: 8,
                        background: '#ff00ff',
                        transform: 'translate(-0.5px, -4px)',
                        pointerEvents: 'none',
                        zIndex: 30,
                      }}
                    />
                  ))}
                  {/* Spacing pill */}
                  <div
                    style={{
                      position: 'absolute',
                      top: `${perp}%`,
                      left: `${pillCenterPct}%`,
                      transform: 'translate(-50%, -130%)',
                      background: '#ff00ff',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '1px 5px',
                      borderRadius: 2,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 31,
                    }}
                  >
                    {spacingPx} px
                  </div>
                </React.Fragment>
              )
            })()}
            {badgeDrag && badgeDrag.hasMoved && smartGuides.y && (() => {
              const { a, b, c, spacingPct, perp } = smartGuides.y
              const spacingPx = Math.round((spacingPct / 100) * 1080)
              const pillCenterPct = (b + c) / 2
              return (
                <React.Fragment>
                  {/* Segment a ↔ b (vertical) */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${perp}%`,
                      top: `${a}%`,
                      height: `${b - a}%`,
                      width: 1,
                      background: '#ff00ff',
                      pointerEvents: 'none',
                      zIndex: 30,
                    }}
                  />
                  {/* Segment b ↔ c (vertical) */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${perp}%`,
                      top: `${b}%`,
                      height: `${c - b}%`,
                      width: 1,
                      background: '#ff00ff',
                      pointerEvents: 'none',
                      zIndex: 30,
                    }}
                  />
                  {[a, b, c].map((v) => (
                    <div
                      key={`smart-y-tick-${v}`}
                      style={{
                        position: 'absolute',
                        left: `${perp}%`,
                        top: `${v}%`,
                        height: 1,
                        width: 8,
                        background: '#ff00ff',
                        transform: 'translate(-4px, -0.5px)',
                        pointerEvents: 'none',
                        zIndex: 30,
                      }}
                    />
                  ))}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${perp}%`,
                      top: `${pillCenterPct}%`,
                      transform: 'translate(8px, -50%)',
                      background: '#ff00ff',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '1px 5px',
                      borderRadius: 2,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 31,
                    }}
                  >
                    {spacingPx} px
                  </div>
                </React.Fragment>
              )
            })()}
            {marquee && (() => {
              const left = Math.min(marquee.startX, marquee.endX)
              const top = Math.min(marquee.startY, marquee.endY)
              const width = Math.abs(marquee.endX - marquee.startX)
              const height = Math.abs(marquee.endY - marquee.startY)
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    top: `${top}%`,
                    width: `${width}%`,
                    height: `${height}%`,
                    border: '1px dashed #1976d2',
                    background: 'rgba(25, 118, 210, 0.08)',
                    pointerEvents: 'none',
                    zIndex: 20,
                  }}
                />
              )
            })()}
              </>
            )}
          </div>
          </div>
        </div>

        {/* Right: font & positioning controls */}
        <Paper
          square
          elevation={0}
          style={{ width: 200, borderLeft: '1px solid #e0e0e0', flexShrink: 0, overflowY: 'auto', ...(sidebarHeight && { height: sidebarHeight }) }}
        >
          {isPreviewMode && (
            <div style={{ padding: 12 }}>
              <Typography variant='caption' color='textSecondary'>
                Previewing the overlay as it will appear on stream. Switch back to Edit to arrange fields.
              </Typography>
            </div>
          )}

          {!isPreviewMode && (
            <SelectByType
              placements={placements}
              fields={fields}
              selection={selection}
              onSelect={(ids, additive) => {
                setPendingField(null)
                selectFields(additive ? [...selection.filter((id) => !ids.includes(id)), ...ids] : ids)
              }}
            />
          )}

          {!isPreviewMode && selection.length === 0 && (
            <div style={{ padding: 12 }}>
              <Typography variant='caption' color='textSecondary'>
                Select a field on the canvas to edit it.
              </Typography>
            </div>
          )}

          {!isPreviewMode && selection.length >= 2 && (
            <MultiSelectPanel
              ids={selection}
              placements={placements}
              fields={fields}
              aspectLocked={isAspectLocked}
              onLockChange={setAspectLock}
              onPatch={updatePlacements}
              onRemove={removePlacements}
            />
          )}

          {!isPreviewMode && selectedFieldId && (() => {
            const editingId = selectedFieldId
            const p = placements.find((pl) => pl.fieldId === editingId)
            const f = fields.find((fl) => fl.key === editingId)
            if (!p) return null
            const isImage = f?.dataType === 'Image'
            const isBar = f?.dataType === 'Bar'
            const isColorBox = f?.dataType === 'ColorBox'
            return (
              <>
                <div style={{ padding: '8px 12px 4px' }}>
                  <Typography variant='subtitle2'>{sentenceCase(f?.label ?? editingId)}</Typography>
                </div>
                {!isImage && !isBar && !isColorBox && (
                  <>
                    <div style={{ padding: '4px 12px' }}>
                      <Typography variant='caption' color='textSecondary'>Preview text</Typography>
                      <TextField
                        placeholder={f?.sampleValue ?? ''}
                        value={previewText[editingId] ?? ''}
                        onChange={(e) => setPreviewText((prev) => ({ ...prev, [editingId]: e.target.value }))}
                        size='small'
                        variant='outlined'
                        fullWidth
                        InputProps={{ style: { fontSize: 12 } }}
                      />
                    </div>
                    {f?.formats?.length > 0 && (
                      <div style={{ padding: '4px 12px' }}>
                        <Typography variant='caption' color='textSecondary'>Format</Typography>
                        <Select
                          value={p.valueFormat ?? f.formats[0].key}
                          onChange={(e) => updatePlacement(editingId, { valueFormat: e.target.value })}
                          fullWidth
                          style={{ fontSize: 12 }}
                        >
                          {f.formats.map((fmt) => (
                            <MenuItem key={fmt.key} value={fmt.key} style={{ fontSize: 12 }}>{fmt.label}</MenuItem>
                          ))}
                        </Select>
                      </div>
                    )}
                    {/* Free text either side of the value — a unit or marker the designer
                        types. Neither renders when the value is empty, so a player with no
                        recorded height leaves no stray unit on air. */}
                    <div style={{ padding: '4px 12px' }}>
                      <Typography variant='caption' color='textSecondary'>Before / after value</Typography>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <TextField
                          value={p.valuePrefix ?? ''}
                          onChange={(e) => updatePlacement(editingId, { valuePrefix: e.target.value })}
                          placeholder='before'
                          fullWidth
                          inputProps={{ style: { fontSize: 12 } }}
                        />
                        <TextField
                          value={p.valueSuffix ?? ''}
                          onChange={(e) => updatePlacement(editingId, { valueSuffix: e.target.value })}
                          placeholder='after'
                          fullWidth
                          inputProps={{ style: { fontSize: 12 } }}
                        />
                      </div>
                    </div>
                    <TextStyleControls
                      targets={[{ placement: p, field: f }]}
                      onPatch={(fn) => updatePlacements([editingId], fn)}
                    />
                  </>
                )}
                {isBar && (
                  <BarFillControl targets={[{ placement: p }]} onPatch={(fn) => updatePlacements([editingId], fn)} />
                )}
                {!isImage && (
                  <BoxSizeControls targets={[{ placement: p }]} onPatch={(fn) => updatePlacements([editingId], fn)} />
                )}
                {isImage && (
                  <>
                    {/* The sponsor area: the designer draws one rectangle and says how the
                        logos sit in it. The count comes from the game and every logo keeps its
                        stored 4:3 crop, so the individual slots are derived at render time. */}
                    {p.fieldId === 'sponsors' && (
                      <div style={{ padding: '4px 12px' }}>
                        <Typography variant='caption' color='textSecondary'>Sponsor layout</Typography>
                        {/* Logos per row is the only shape control: five (the most a game
                            carries) fills one line and one gives a single column, so the old
                            row-vs-grid choice said nothing this does not. */}
                        <StepperRow
                          label='Per row' value={p.perRow ?? SPONSOR_DEFAULT_PER_ROW} step={1}
                          onChange={(v) => updatePlacement(editingId, { perRow: Math.max(1, Math.min(5, Math.round(v))) })}
                        />
                        {/* Alignment positions the block inside the area — and a short
                            final row (five at three per row leaves two). */}
                        <AlignRow
                          hAlign={p.hAlign ?? 'center'}
                          vAlign={p.vAlign ?? 'middle'}
                          onChange={(patch) => updatePlacement(editingId, patch)}
                        />
                        <StepperRow
                          label='Gap' value={+(((p.gap ?? 0.08) * 100).toFixed(1))} suffix='%' step={1}
                          onChange={(v) => updatePlacement(editingId, { gap: Math.max(0, Math.min(50, v)) / 100 })}
                        />
                      </div>
                    )}
                    <ImageSizeControls
                      targets={[{ placement: p }]}
                      isLocked={isAspectLocked}
                      onLockChange={setAspectLock}
                      onPatch={(fn) => updatePlacements([editingId], fn)}
                    />
                  </>
                )}
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ padding: '4px 12px 0' }}>
                  <Typography variant='caption' color='textSecondary' style={{ fontWeight: 500 }}>Position</Typography>
                </div>
                <StepperRow label='X' value={p.x ?? 0} suffix='%' step={0.5} precision={2} onChange={(v) => updatePlacement(editingId, { x: Math.max(0, Math.min(100, v)) })} />
                <StepperRow label='Y' value={p.y ?? 0} suffix='%' step={0.5} precision={2} onChange={(v) => updatePlacement(editingId, { y: Math.max(0, Math.min(100, v)) })} />
                <Divider style={{ margin: '8px 0 4px' }} />
                <div style={{ padding: '4px 12px' }}>
                  <Button
                    size='small'
                    color='secondary'
                    startIcon={<DeleteIcon style={{ fontSize: 16 }} />}
                    onClick={() => removePlacement(editingId)}
                    style={{ fontSize: 11 }}
                  >
                    Remove field
                  </Button>
                </div>
              </>
            )
          })()}
        </Paper>
      </div>
      <Snackbar
        open={savedNotice}
        autoHideDuration={2500}
        onClose={() => setSavedNotice(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='success' variant='filled' onClose={() => setSavedNotice(false)}>Saved</Alert>
      </Snackbar>
    </div>
  )
}
